// English catalog. This is the reference shape; every other catalog must match
// it exactly. Keep messages whole (do not build sentences from fragments) and
// put every user-visible string here, including accessibility labels.

export const en = {
  app: {
    name: 'Sensing',
    tagline: 'Field notebook for your sensors',
  },
  home: {
    lede: 'Readings from the sensors in your care, in one place.',
    accountHint: 'Sign-in is through Google or Apple. No password is created here.',
    signIn: 'Sign in',
    aboutLink: 'About Sensing',
  },
  about: {
    title: 'About Sensing',
    lede: 'Sensing is a web application for reading, organizing and maintaining the sensors in your care. You watch what they report, and you change how they report it.',
    whatTitle: 'What it does',
    whatBody:
      'An account owns sources: ESP32 sensor devices, fixed-purpose sensors reached through a gateway, and external feeds that push their own data. The app shows what each one is reporting and lets the owner adjust it.',
    capability_feeds:
      'A dashboard of every channel the account owns: the newest value and a recent history, drawn as a chart or listed as readings.',
    capability_groups:
      'Groups that describe a place, divided into sections such as rooms, which can be shared read-only with a single-use invite link.',
    capability_devices:
      'Configuration of a device over the air: how often it reports, which modules it runs, its time zone and its Wi-Fi networks.',
    capability_external:
      'External feeds, for data arriving over HTTP or MQTT from something that is not a Sensing device.',
    capability_sensors:
      'Enrollment of Zigbee and Bluetooth sensors through a gateway running the matching bridge module.',
    accountTitle: 'Your account',
    accountBody:
      'Sign-in is through Google or Apple; no password is created here and no email is sent. The account is used to keep each owner’s devices and readings separate from everyone else’s.',
    backHome: 'Back to the start',
  },
  legal: {
    privacyLink: 'Privacy policy',
    termsLink: 'Terms of service',
    privacyTitle: 'Privacy policy',
    termsTitle: 'Terms of service',
    updated: 'Last updated {date}',
    contact: 'Questions about this page: {email}',
    privacy_who_title: 'Who is responsible',
    privacy_who_body:
      'The Sensing service is operated by the operator of the deployment you are using. This page describes what that service stores and why. The operator is the data controller for it.',
    privacy_google_title: 'What we receive from Google or Apple',
    privacy_google_body:
      'If you sign in with Google, Google tells us a stable identifier for your Google account, your email address, whether that address is verified, and your name and profile picture if your account has them. We request only the openid, email and profile scopes. We do not request access to Gmail, Drive, Calendar, contacts, or any other Google service, and we never see your Google password. If you sign in with Apple, Apple tells us a stable identifier, an email address (which may be a private relay address Apple creates for you), and your name the first time you sign in. We keep the identifier, the email address, whether it is verified, and the name; we do not keep the picture.',
    privacy_collected_title: 'What the service stores',
    privacy_collected_body:
      'Beyond what the sign-in provider returns, the service stores what is needed to run it:',
    privacy_collected_account:
      'Your account: the provider identifier, email address, whether the address is verified, and your display name. Never a password.',
    privacy_collected_readings:
      'The sources you own and the readings they send: channel keys, values, units, observation and ingestion times, and the time-quality label.',
    privacy_collected_devices:
      'Device records: the public identifier, hardware and firmware version, reported status, certificates, and the configuration you asked for.',
    privacy_collected_wifi:
      'Wi-Fi network names a device reports, and the passwords you enter for it. Passwords are sealed in your browser to a key the device publishes, so the service stores ciphertext it cannot read.',
    privacy_collected_technical:
      'Technical records: a hash of your session token in a cookie, audit entries for administrative actions, and counted metrics that carry no account identity.',
    privacy_purpose_title: 'Why we use it',
    privacy_purpose_body:
      'To sign you in and keep your session, to show you your own data and nobody else’s, to deliver configuration to your devices, to keep the service working and secure, and to answer you when you contact us. We do not use Google user data for advertising, profiling, or training machine-learning models, and we do not sell it.',
    privacy_stored_title: 'Where it is kept',
    privacy_stored_body:
      'The service’s servers and database are in the European Union. Cloudflare delivers the web app and passes your requests on to those servers, and Google or Apple handles the sign-in you choose. These companies may process data in the United States, under the EU–US Data Privacy Framework or the European Commission’s standard contractual clauses. Data is transmitted over HTTPS and MQTT over TLS. Passwords for Wi-Fi are stored sealed as described above, and sign-in secrets and device private keys are kept outside the application database.',
    privacy_cookies_title: 'Cookies and local storage',
    privacy_cookies_body:
      'The service sets a session cookie, which is HttpOnly and Secure, a CSRF cookie, and while you sign in a login cookie that expires within 10 minutes. All three are needed to sign in and to submit forms, and none is used for advertising or analytics. Your language and appearance preferences, and an invite link you opened before signing in, are kept in your browser’s storage and are never sent to the server. There is no third-party analytics, advertising, or tracking script on any page.',
    privacy_sharing_title: 'Who else sees it',
    privacy_sharing_body:
      'We do not sell your data and do not share it for advertising. It reaches others only in these cases:',
    privacy_sharing_processors:
      'Companies that run parts of the service for the operator: the hosting provider, Cloudflare, and Google or Apple for the sign-in you choose. They act on the operator’s instructions.',
    privacy_sharing_legal: 'Where the law requires it, or to protect the service and its users from abuse.',
    privacy_sharing_noAds: 'Never to advertisers, data brokers, or anyone building a profile of you.',
    privacy_retention_title: 'How long it is kept',
    privacy_retention_body:
      'Readings are kept until you delete them: a stream, a range of readings, a source, or the whole account. A source you remove can keep its history in an archive you can still read and delete. Deleting your account removes everything it owns at once and releases its devices; only the account’s internal number stays in the audit log, without your name or email, to show who changed what. Session records expire after 7 days of inactivity or 30 days in total, and records of input the service had to reject after 30 days.',
    privacy_rights_title: 'Your choices',
    privacy_rights_body:
      'Under the GDPR you have these rights. Two of them are in the app, under Account: Download my data gives you a copy of everything the account holds, in a machine-readable file, and Delete account erases it. For anything else, write to the address below:',
    privacy_rights_access: 'Know what is held about you and get a copy of it (Download my data, in the app).',
    privacy_rights_correct: 'Correct anything that is wrong.',
    privacy_rights_delete: 'Delete your account and the data attached to it (Delete account, in the app).',
    privacy_rights_object: 'Object to a use of your data, or ask us to restrict it.',
    privacy_rights_complain:
      'Complain to a supervisory authority. In Croatia that is the Agency for Personal Data Protection (AZOP).',
    privacy_children_title: 'Children',
    privacy_children_body:
      'The service is not intended for children under 16, and we do not knowingly create accounts for them.',
    privacy_changes_title: 'Changes',
    privacy_changes_body:
      'If this policy changes, the date at the top changes with it. A material change is announced in the app before it takes effect.',
    terms_service_title: 'The service',
    terms_service_body:
      'Sensing is a hosted application for monitoring and maintaining sensors. It is offered as it is, for the operator’s own use and for accounts the operator approves.',
    terms_account_title: 'Your account',
    terms_account_body:
      'You sign in through Google or Apple and are responsible for activity under your account. Tell the operator promptly if you believe someone else has access to it.',
    terms_use_title: 'Acceptable use',
    terms_use_body: 'You agree not to:',
    terms_use_lawful: 'Use the service unlawfully, or to store data you have no right to hold.',
    terms_use_access:
      'Attempt to reach another account’s data, or to bypass the authentication and authorization the service applies.',
    terms_use_devices:
      'Operate devices in a way that endangers people, property, or the network they connect to.',
    terms_data_title: 'Your data',
    terms_data_body:
      'Your readings and device configuration remain yours. You can delete them at any time; the privacy policy explains what deletion removes and what remains.',
    terms_availability_title: 'Availability and changes',
    terms_availability_body:
      'The service is provided without a guarantee of uninterrupted availability. Features may change, and a device update may briefly interrupt readings. Planned interruptions are announced where the operator can.',
    terms_liability_title: 'Liability',
    terms_liability_body:
      'To the extent the law allows, the operator is not liable for indirect or consequential loss arising from use of the service, including loss of readings. Nothing here limits liability that cannot be limited by law.',
    terms_changes_title: 'Changes to these terms',
    terms_changes_body:
      'If these terms change, the date at the top changes with it, and a material change is announced in the app before it takes effect.',
  },
  nav: {
    dashboard: 'Data feeds',
    groups: 'Groups',
    sources: 'Sources',
    manage: 'Manage',
    admin: 'Administration',
    account: 'Account',
    signOut: 'Sign out',
    refresh: 'Refresh',
    back: 'Back',
    menu: 'Menu',
    primaryNavigation: 'Primary navigation',
    manageNavigation: 'Management navigation',
    tabs: {
      feeds: 'Feeds',
      groups: 'Groups',
      sources: 'Sources',
      admin: 'Admin',
      account: 'Account',
    },
  },
  dashboard: {
    title: 'Data feeds',
    description: 'The latest readings from every channel on this account.',
    noChannels: 'This source has no channels yet.',
    noDatastream: 'No readings yet. A channel gets its datastream from the first sample it receives.',
    openReadings: 'Open readings',
    latest: 'Latest',
    recentPoints: 'Newest {count} readings, drawn oldest first.',
    partial: 'Showing {shown} of {total} channels.',
    sourcesError: 'Could not load the sources for this account.',
  },
  common: {
    loading: 'Loading…',
    retry: 'Try again',
    retryingAutomatically: 'Trying again automatically…',
    error: 'Something went wrong',
    empty: 'Nothing here yet',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied to clipboard',
    copyFailed: 'Could not copy; select the text and copy it manually',
    confirm: 'Confirm',
    dismiss: 'Dismiss',
    details: 'Details',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    unknown: 'Unknown',
    none: 'None',
    notAvailable: 'Not available',
    yes: 'Yes',
    no: 'No',
    lastUpdated: 'Last updated {time}',
    staleData: 'Showing data from {time}; it may be out of date',
    refreshing: 'Refreshing…',
    forbiddenTitle: 'Not available to this account',
    forbiddenBody: 'You do not have access to this screen.',
    loadMore: 'Load more',
    of: 'of',
  },
  theme: {
    label: 'Appearance',
    light: 'Light',
    black: 'Black',
    system: 'System',
  },
  language: {
    label: 'Language',
    en: 'English',
    hr: 'Croatian',
  },
  auth: {
    title: 'Sign in',
    subtitle: 'Use the account that owns your devices.',
    continueWithProvider: 'Continue with {provider}',
    providerUnavailable: 'This sign-in provider is not configured for this deployment.',
    noProviders: 'No sign-in provider is configured. Contact the service operator.',
    error: 'Sign-in did not complete. Please try again.',
    cancelled: 'Sign-in was cancelled.',
    awaitingApprovalTitle: 'Waiting for approval',
    awaitingApproval:
      'Your account is created. Sensing is in a closed beta, so an administrator has to approve it before you can sign in.',
    expired: 'Your session expired. Please sign in again.',
    signOutConfirmTitle: 'Sign out?',
    signOutConfirmBody: 'You will need to sign in again to reach your sources.',
    signedOut: 'Signed out',
  },
  account: {
    title: 'Account',
    description: 'Your session identity and local preferences.',
    userId: 'Effective account',
    actorUserId: 'Session owner',
    sessionId: 'Session',
    role: 'Role',
    roleOwner: 'Owner',
    roleAdmin: 'Administrator',
    impersonating: 'Impersonating',
    preferencesNote: 'Language and appearance are stored in this browser only.',
    dataTitle: 'Your data',
    exportBody:
      'Download everything this account holds as one JSON file: your sign-in identities, sources, groups and every reading.',
    exportButton: 'Download my data',
    deleteBody:
      'Deleting the account removes it and everything it owns, at once and for good: readings, sources, groups and shares. Devices are released and reset, so someone else can claim them. A record that the account existed, without your name or email, stays in the audit log.',
    deleteButton: 'Delete account',
    deleteTitle: 'Delete this account?',
    deleteConfirmBody:
      'This cannot be undone. Download your data first if you want to keep it. Type {word} to confirm.',
    deleteWord: 'DELETE',
    deleteConfirmLabel: 'Confirmation',
    deleteWhileImpersonating:
      'An account cannot be deleted while you act as it; delete it from the admin page.',
    deleteLastAdmin: 'You are the only administrator. Make another account an administrator first.',
  },
  sources: {
    archived: 'Removed',
    archivedHint: 'The history of sources you removed. Open one to read or delete it.',
    title: 'Sources',
    description: 'Devices and external feeds owned by this account.',
    empty: 'No sources yet',
    emptyHint: 'Add a device with a pairing code, or create an external feed.',
    addDevice: 'Add a device',
    addExternal: 'Create external feed',
    kindDevice: 'Device',
    kindExternal: 'External feed',
    kindLabel: 'Kind',
    stateActive: 'Active',
    stateDisabled: 'Disabled',
    stateRetired: 'Retired',
    statusLabel: 'Status',
    nameLabel: 'Name',
    unnamed: 'Unnamed source',
  },
  source: {
    archive: 'Removed source: its history',
    archiveHint:
      'This is the history of a source you removed. Nothing new is added to it. You can read it, rename it or delete it.',
    archivedStreams: 'Streams',
    inactive: 'No longer reported',
    inactiveHint:
      'The source does not send these any more, for example because a module was removed. Their history stays until you delete it.',
    title: 'Source',
    channels: 'Channels',
    channelsEmpty: 'No channels are declared for this source yet.',
    channelsEmptyHint: 'Channels appear here once the source declares them.',
    channelCount: { one: '{count} channel', other: '{count} channels' },
    device: 'Device',
    deviceDetails: 'Device details',
    externalFeed: 'External feed',
    noReadingsYet: 'No readings yet',
    selectChannel: 'Select a channel to view its readings.',
    rename: 'Rename',
    renameTitle: 'Rename',
    nameLabel: 'Name',
    nameHelp: 'Shown in lists and on the dashboard. Leave empty to clear it.',
    renamed: 'Name saved',
  },
  modules: {
    title: 'Modules',
    action: 'Modules',
    intro:
      'Modules are what the device does: each one reads a sensor, drives an output or runs logic. Add them from the catalog below, set them up, and save to send the device a new revision.',
    selected: 'On this device',
    none: 'No modules yet',
    noneHint: 'Add one from the catalog below.',
    available: 'Add a module',
    availableEmpty: 'Every module in the catalog is already on this device.',
    catalogEmpty: 'The catalog is empty. An administrator publishes modules in Administration.',
    add: 'Add',
    remove: 'Remove',
    enabled: 'Enabled',
    version: 'Version {version}',
    update: 'Update to {version}',
    builtIn: 'Built into the firmware',
    needsAwake: 'Keeps the device awake',
    switchesMode:
      'Saving also switches this device to always on, because {name} needs it awake all the time.',
    instanceTitle: 'Copy',
    instanceLabel: 'Name of this copy',
    instanceHelp:
      'Tells copies of the same module apart, for example “outside”. Lowercase letters, digits, - and _. Its readings are stored under this name, so renaming it starts new streams.',
    instanceInvalid:
      'Each copy needs its own name: up to 15 lowercase letters, digits, - or _, starting with a letter or digit.',
    scheduleTitle: 'How often',
    minInterval: 'Read at most every (minutes)',
    minIntervalHint:
      'The device reads all its modules once per reading interval (Operation tab). With a value here, it skips this module until that many minutes have passed since this module last read, so the spacing is a multiple of the reading interval: 60 minutes on a 15-minute interval reads every fourth time.',
    minIntervalEvery: 'Every reading interval',
    settings: 'Settings',
    signals: 'Inputs',
    signalHelp: 'The reading from another module on this device that this input uses.',
    signalNone: 'Not connected',
    signalNoChoices: 'No module on this device produces a matching reading.',
    pins: 'Pins',
    pinSda: 'SDA pin',
    pinScl: 'SCL pin',
    busSpeed: 'Bus speed (Hz)',
    pin: 'Pin',
    pinOptional: 'Optional. Leave empty if this module should not drive a pin.',
    jsonInvalid: 'Not valid JSON yet; the last valid value is kept.',
    jsonNeedsObject: 'Needs a JSON object, in braces.',
    jsonNeedsArray: 'Needs a JSON array, in brackets.',
    edge: 'Count pulses on',
    edgeRising: 'Rising edge',
    edgeFalling: 'Falling edge',
    edgeBoth: 'Both edges',
    unsupportedPort: 'This port type cannot be assigned here.',
    notInCatalog: 'Not in the catalog; kept unchanged when you save.',
    deviceState: 'Device: {state}',
    readings: 'Reports: {list}',
    tooMany: 'At most {count} modules can be enabled at once.',
    save: 'Save modules',
    saving: 'Saving…',
    saved: 'Saved as revision {revision}. The device installs it when it next wakes.',
    savedAlwaysOn: 'Saved as revision {revision}. The device is connected and installs it within seconds.',
    unchanged: 'No changes to save.',
    failed:
      'The device refuses revision {revision} because it already failed to apply it. Once the cause is fixed, send the same modules again as a new revision.',
    resend: 'Send again',
    zigbeeHint: 'Once this bridge runs on the device, enroll Zigbee sensors from the device’s page.',
    bleHint: 'Once this bridge runs on the device, enroll BLE sensors from the device’s page.',
    stateInstalling: 'installing',
    stateActive: 'running',
    stateDisabled: 'disabled',
    stateError: 'error',
  },
  device: {
    firmware: {
      title: 'Firmware',
      running: 'Running',
      notReported: 'Not reported yet',
      upToDate: 'Up to date.',
      available: 'Version {version} is available.',
      availableHint:
        'About {size} KiB. The device downloads it in the background, restarts into it and keeps it only if it starts correctly.',
      update: 'Update to {version}',
      confirmTitle: 'Update to {version}?',
      confirmBody:
        'The device downloads the update the next time it is online, then restarts. Outputs switch off and readings pause during the restart. If the new firmware does not start correctly, the device returns to {running} by itself.',
      stageWaiting: 'Update to {version} requested. The device picks it up the next time it is online.',
      stageDownloading: 'Downloading {version}…',
      stageInstalling: 'Installing {version}; the device restarts.',
      stageConfirming: '{version} started; waiting for the device to report it.',
      stageFailed: 'The update to {version} did not complete ({reason}). The device kept its firmware.',
      reasonUnknown: 'no reason given',
      failedHint: 'This release cannot be offered to this device again; a newer one can.',
    },
    classConfigurable: 'Configurable device',
    classFixed: 'Fixed sensor',
    statusTitle: 'Status',
    reported: 'Status reported',
    notReported: 'No status reported yet',
    stateAwake: 'Awake',
    stateSleeping: 'Sleeping',
    stateDisconnected: 'Disconnected',
    operationMode: 'Operating mode',
    modeDeepSleep: 'Deep sleep',
    modeAlwaysOn: 'Always on',
    receivedAt: 'Status received',
    lastContactAt: 'Last live contact',
    neverContacted: 'No confirmed live contact',
    retainedReplay: 'Retained or replayed status',
    freshnessNote: 'A recent receipt or an online gateway does not prove the sensor is online right now.',
    deepSleepNote:
      'Deep sleep is expected operation, not a fault. The device wakes on its configured cadence.',
    fixedNote: 'Fixed sensors have no configuration, modules, or commands.',
    hardwareRevision: 'Hardware revision',
    ownedSince: 'Owned since',
  },
  readings: {
    title: 'Readings',
    description: 'Newest readings first. A page is not the entire history.',
    empty: 'No readings yet',
    emptyHint: 'Readings appear here after the source sends its first sample.',
    noDatastream: 'This channel has no readings yet. Its datastream is created by the first sample.',
    tableView: 'Table',
    chartView: 'Chart',
    chartTitle: 'History',
    chartHint: 'Drag the slider to zoom. The table keeps its own pages.',
    chartBooleanHint: 'A boolean is a state. The line stays flat until the next sample changes it.',
    chartNonNumeric: 'Text values have no shape over time, so this channel is shown as values only.',
    chartNotEnoughPoints: 'Not enough readings in this range to draw a chart.',
    chartNoPoints: 'No readings in this range.',
    rangeEmpty: 'No readings in this range. Try a longer one.',
    rangeLabel: 'Range',
    range_1h: '1 h',
    range_6h: '6 h',
    range_24h: '24 h',
    range_7d: '7 d',
    range_30d: '30 d',
    range_all: 'All',
    rangeSummary: '{count} points over the last {range}.',
    rangeAllSummary: '{count} points, from the newest backwards.',
    rangeTruncated: 'Showing the newest {count} points; pick a shorter range to see all of it.',
    columnChannel: 'Channel',
    columnValue: 'Value',
    columnUnit: 'Unit',
    columnObservedAt: 'Observed',
    columnIngestedAt: 'Ingested',
    columnQuality: 'Time quality',
    qualitySynchronized: 'Synchronized',
    qualityEstimated: 'Estimated',
    qualityUnknown: 'Unknown',
    observationUnknown: 'Observation time unknown',
    observationUnknownHint:
      'The source did not report when this reading was taken; only ingestion time is known.',
    observationEstimated: 'Estimated observation time',
    ingestedNote: 'Ingestion time is when the backend stored the reading.',
    pageNote: 'Loaded {count} readings. More may exist.',
    previousPage: 'Previous',
    booleanTrue: 'True',
    booleanFalse: 'False',
  },
  groups: {
    title: 'Groups',
    description:
      'Organize your feeds into groups for places, such as a house or a field, and share them with others to view.',
    empty: 'No groups yet',
    emptyHint: 'Create a group, then add feeds to it.',
    nameLabel: 'Group name',
    namePlaceholder: 'Greenhouse',
    nameInvalid: 'Use 1 to 64 characters.',
    create: 'Create group',
    creating: 'Creating…',
    feedCount: { one: '{count} feed', other: '{count} feeds' },
    loading: 'Loading groups…',
    notFound: 'This group does not exist, or you do not own or view it.',
    delete: 'Delete group',
    deleteConfirm: 'Delete this group? Its feeds and readings are kept.',
    feeds: 'Feeds in this group',
    noFeeds: 'This group has no feeds yet.',
    addFeed: 'Add a feed',
    addFeedPlaceholder: 'Choose a feed',
    add: 'Add',
    remove: 'Remove {name} from this group',
    allAdded: 'Every feed you own is already in this group.',
    noOwnedFeeds: 'Feeds appear here once a source has reported a reading.',
    back: 'All groups',
    noFeedsHint: 'Add feeds on the Feeds tab.',
    noSection: 'Other',
    locationLabel: 'Location',
    locationPlaceholder: 'Zagreb, Ilica 1',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What this place is',
    saveDetails: 'Save details',
    sectionLabel: 'Section',
    sectionPlaceholder: 'Bedroom',
    sectionOf: 'Section of {name}',
    leave: 'Stop viewing',
    leaveConfirm: 'Stop viewing this group? You will need a new invite to see it again.',
    tabOverview: 'Overview',
    tabFeeds: 'Feeds',
    tabDetails: 'Details',
    tabSharing: 'Sharing',
    sharedTitle: 'Shared with you',
    sharedBy: 'Shared by {name}',
    sharedWithYou: 'Shared with you',
    viewerCount: { one: 'Shared with {count} person', other: 'Shared with {count} people' },
  },
  sharing: {
    title: 'Share this group',
    description:
      'An invite link lets one person view this group: its details, feeds and readings, never your devices or their settings. Feeds you add later are shared too. A link works once and expires after seven days.',
    createLink: 'Create invite link',
    newLink: 'New invite link',
    newLinkHint: 'Send it to the person you invite. It is shown only now and works until {date}.',
    copy: 'Copy',
    copied: 'Copied',
    openLinks: 'Open invite links',
    noOpenLinks: 'No open invite links.',
    linkCreated: 'Created {date}',
    linkExpires: 'Expires {date}',
    cancelLink: 'Cancel this invite link',
    viewers: 'People who view this group',
    noViewers: 'Nobody else views this group.',
    unnamedViewer: 'Unnamed account',
    viewerSince: 'since {date}',
    removeViewer: 'Remove {name}',
    removeViewerConfirm: 'Remove {name}? They stop seeing this group at once.',
  },
  invite: {
    title: 'Invite to a group',
    body: '{owner} invites you to view the group “{group}”.',
    someone: 'Someone',
    disclosure: 'If you accept, {owner} will see your name and email. You can stop viewing at any time.',
    expires: 'This invite works once and until {date}.',
    accept: 'Accept and open',
    openGroup: 'Open the group',
    decline: 'Not now',
    ownGroup: 'This is your own group, so there is nothing to accept.',
    closed: 'This invite no longer works',
    closedHint: 'It was used, cancelled or has expired. Ask for a new link.',
    pasteAnother: 'Use another link',
    noLink: 'Open the invite link you received, or paste it here.',
    pasteLabel: 'Invite link',
    pastePlaceholder: 'https://…/invite#…',
    pasteInvalid: 'This is not an invite link.',
    open: 'Open invite',
    installFirst:
      'You opened an invite. Install the app, sign in, then paste the invite link on the Groups page.',
    copyLink: 'Copy invite link',
  },
  wifi: {
    title: 'Wi-Fi',
    action: 'Wi-Fi',
    unsupported:
      'This device has not reported remote Wi-Fi support yet. It appears after the device runs firmware that supports it and reports in.',
    saved: 'Saved networks',
    none: 'No networks reported',
    connected: 'Connected',
    forget: 'Forget',
    lastNetwork:
      'A device always keeps at least one network. Add another before forgetting this one; otherwise only its setup page can change it.',
    reportedAt: 'As the device reported {time}',
    add: 'Add a network',
    ssid: 'Network name',
    ssidTooLong: 'At most 32 bytes.',
    open: 'Open network (no password)',
    password: 'Password',
    passwordHelp: '8 to 63 characters. Adding a saved network name replaces its password.',
    sealedNote:
      'The password is encrypted in this browser to a key only the device holds. The server stores and forwards it but cannot read it, and deletes it once it is sent.',
    addButton: 'Add network',
    sealUnavailable: 'This browser cannot encrypt the password here. Open the app over HTTPS.',
    changes: 'Recent changes',
    changeAdd: 'Add {ssid}',
    changeRemove: 'Forget {ssid}',
    changesHint:
      'A sleeping device picks up changes at its next wake; a change not delivered within an hour expires.',
    changesHintAlwaysOn:
      'The device is connected and picks up changes within seconds; a change not delivered within an hour expires.',
    state: {
      queued: 'Queued',
      sent: 'Sent',
      applied: 'Applied',
      expired: 'Expired',
    },
  },
  zigbee: {
    title: 'Zigbee sensors',
    action: 'Zigbee sensors',
    intro:
      'Enroll a Zigbee sensor through this gateway. Enter the EUI-64 and install code from the sensor label, then power the sensor on within three minutes.',
    gatewayNote: 'The gateway must run the Zigbee bridge module in always-on mode.',
    eui64: 'EUI-64',
    eui64Help: '16 hexadecimal digits from the label',
    installCode: 'Install code',
    installCodeHelp: 'From the label, including its checksum',
    name: 'Name (optional)',
    enroll: 'Enroll sensor',
    enrolling: 'Enrolling…',
    invalidEui64: 'Use 16 hexadecimal digits.',
    invalidCode: 'Use 16, 20, 28 or 36 hexadecimal digits.',
    requested: 'The gateway is opening its network. Power the sensor on now.',
    history: 'Enrollments',
    empty: 'No sensors enrolled through this gateway yet.',
    pending: 'Waiting for the sensor',
    completed: 'Enrolled',
    expired: 'Expired',
    openSensor: 'Open sensor',
    notGateway: 'This device is not running the Zigbee bridge module.',
  },
  ble: {
    title: 'BLE sensors',
    action: 'BLE sensors',
    intro:
      'Enroll a BLE sensor through this gateway. Enter the address and key from the sensor label, then power the sensor on within ten minutes. Its first reading that opens with this key enrolls it.',
    gatewayNote: 'The gateway must run the BLE bridge module in always-on mode.',
    address: 'Address',
    addressHelp: '12 hexadecimal digits from the label, with or without colons',
    key: 'Key',
    keyHelp: '32 hexadecimal digits from the label. It is stored encrypted and never shown again.',
    name: 'Name (optional)',
    enroll: 'Enroll sensor',
    enrolling: 'Enrolling…',
    invalidAddress: 'Use 12 hexadecimal digits.',
    invalidKey: 'Use 32 hexadecimal digits.',
    requested: 'Waiting for the sensor’s first reading. Power it on now.',
    history: 'Enrollments',
    empty: 'No BLE sensors enrolled through this gateway yet.',
    pending: 'Waiting for the sensor',
    completed: 'Enrolled',
    expired: 'Expired',
    openSensor: 'Open sensor',
    notGateway: 'This device is not running the BLE bridge module, or the sensor is already enrolled.',
    notConfigured: 'BLE sensor enrollment is not set up on this server yet.',
  },
  configure: {
    title: 'Configure',
    action: 'Configure',
    intro:
      'Operation and Modules together make up the device’s configuration. Every save on either tab becomes a new numbered revision that is sent to the device; the bar below shows whether the device runs it yet. Wi-Fi networks are sent separately.',
    tabs: {
      operation: 'Operation',
      modules: 'Modules',
      wifi: 'Wi-Fi',
    },
  },
  config: {
    timezone: 'Time zone',
    timezoneHelp:
      'The device’s local time, for modules that act at times of day, such as a rule that switches at 7:00. Empty means UTC.',
    timezoneUtc: 'UTC',
    timezoneNone: 'No such time zone',
    timezoneUse: 'Use {zone}',
    title: 'Operation',
    notConfigured: 'Not configured yet',
    notConfiguredHint: 'Choose how the device runs and save; that sends the device its first configuration.',
    operationMode: 'Operating mode',
    deepSleep: 'Deep sleep',
    alwaysOn: 'Always on',
    intervalSeconds: 'Reading interval (seconds)',
    intervalDeepSleepHelp:
      'How often the device wakes, reads every module and reports. It sleeps in between, which saves power, so changes reach it only when it wakes.',
    intervalAlwaysOnHelp:
      'How often the device reads every module and reports. It stays connected, so changes reach it within seconds, and counters and outputs keep working between readings.',
    intervalRange: 'Allowed for this mode: {min}–{max} seconds.',
    deepSleepBlocked:
      'Deep sleep is not available while {names} is enabled, because it needs the device awake all the time. Turn it off on the Modules tab first.',
    deepSleepUnavailable: 'Deep sleep (not available with {names})',
    openModules: 'Go to Modules',
    rejected: 'The server did not accept this change',
    desiredState: 'How the device runs',
    otaKept: 'A firmware update is waiting for this device; saving keeps it.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved as revision {revision}. The bar above shows when the device runs it.',
    conflictHelp:
      'Someone saved a newer revision while you were editing. It is shown now; make your change again.',
    unchanged: 'No changes to save.',
    loading: 'Loading…',
  },
  delivery: {
    title: 'Configuration delivery',
    none: 'No configuration saved yet. Saving on Operation or Modules sends the device its first one.',
    sending: 'Saved. Sending it to the device now…',
    sentSleeping: 'Sent. The device sleeps between readings, so it picks this up when it next wakes.',
    sentConnected: 'Sent. The device stays connected and confirms a change within seconds.',
    applying: 'The device is applying it now…',
    running: 'The device is up to date: it runs the saved configuration.',
    failed: 'The device could not apply the saved configuration ({code}) and keeps running what it had.',
    showDetails: 'Details',
    hideDetails: 'Hide details',
    explained:
      'Every save is a numbered revision that is sent to the device. Only the device’s own report confirms it runs one; until then the server cannot know.',
    onDevice: 'On the device',
    upToDate: 'Up to date',
    behind: 'Older than what was saved',
    savedAt: 'Saved',
    sentAt: 'Sent to the device',
    notSent: 'Not sent yet',
    notReported: 'Not reported yet',
    deviceMode: 'Operating mode on the device',
    firmware: 'Firmware',
    reportedAt: 'Device last reported',
    lastFailure: 'Last failure',
    lastFailureValue: 'The device rejected the saved configuration ({code})',
    revisions: 'Revision numbers',
    revisionPair: 'Saved {saved}, device runs {device}',
    revisionOnly: 'Saved {saved}, nothing applied yet',
    reportHint: 'This is the device’s own last report. It does not prove the device is online now.',
    reportHintMissing:
      'The device has not reported what it runs yet, so whether it is up to date is unknown.',
  },
  onboarding: {
    elsewhereTitle: 'This device belongs to another account',
    elsewhereBody:
      'Its owner has to remove it from their account first. Then get a new code and pair it again.',
    stepOf: 'Step {step} of {total}',
    next: 'Next',
    back: 'Back',
    codeTitle: 'Get a pairing code',
    codeBody:
      'This one-time code links the device to your account. Copy it now: you will paste it on the device in step 4.',
    getCode: 'Get code',
    codeExpires: 'The code is valid until {time}.',
    powerTitle: 'Plug in the device',
    powerBody: 'A new device opens its own setup Wi-Fi a few seconds after you plug it in.',
    powerHint: 'Set it up before? Hold its setup button while plugging it in, until the light turns blue.',
    joinTitle: "Join the device's Wi-Fi",
    joinBody: "Open your phone's camera, point it at the QR code on the device's label and tap Join.",
    joinHint:
      'No QR code? In Wi-Fi settings, join the network whose name starts with Sensing- and type the password from the label.',
    joined: "I'm connected",
    connectTitle: 'Connect it to your Wi-Fi',
    connectBody:
      'A setup page opens by itself. Choose your Wi-Fi, type its password, paste the code and tap Save and connect.',
    connectHint: "Page didn't open? Go to <link>http://192.168.4.1</link> in your browser.",
    connected: 'Done',
    waitTitle: 'Almost there',
    waitBody:
      'The device joins your Wi-Fi and checks in with your account. Your phone returns to your own Wi-Fi by itself.',
    waiting: 'Waiting for the device…',
    waitOffline:
      "Can't reach the server yet. If your phone is still on the device's Wi-Fi, switch back to your own.",
    expiredTitle: 'The code expired',
    expiredBody:
      'Get a new code and enter it on the setup page again. The device keeps the Wi-Fi details you gave it.',
    failedTitle: 'That code did not work',
    failedBody: 'Get a new code and enter it on the setup page again.',
    newCode: 'Get a new code',
    doneTitle: 'Your device is ready',
    doneBody: 'It is linked to your account. Give it a name you will recognise.',
    nameLabel: 'Name',
    namePlaceholder: 'Greenhouse',
    chooseModules: 'Choose what it measures',
    finish: 'Finish',
    welcomeTitle: 'Welcome to Sensing',
    welcomeBody:
      'Start by adding your first device. It takes a few minutes; have the device and its label at hand.',
    welcomeStart: 'Add a device',
    welcomeExternal: 'Connect an external feed instead',
  },
  pairing: {
    title: 'Add a device',
    copy: 'Copy code',
  },
  external: {
    title: 'Create an external feed',
    description:
      'An external feed is any program, service or device that sends readings to Sensing itself: a script, a weather station, Node-RED, Home Assistant or another cloud.',
    name: 'Feed name',
    nameHelp: 'Shown in your sources and on the dashboard. You can rename it later.',
    namePlaceholder: 'Rooftop weather station',
    connector: 'How will it send data?',
    connectorHelp: 'Pick what your sender can do. This cannot be changed later; create another feed instead.',
    connectorHttp: 'HTTP push',
    connectorHttpHelp:
      'It makes an HTTPS POST request for each reading, with a token in a header. Best for scripts, cron jobs, cloud functions and webhooks.',
    connectorMqtt: 'MQTT',
    connectorMqttHelp:
      'It publishes each reading as an MQTT message over TLS, signing in with a username and password. Best for devices and tools that already speak MQTT.',
    channels: 'Channels',
    channelsHelp:
      'A channel is one kind of value the feed sends, such as temperature. Every reading names its channel, and each channel becomes its own data stream with a chart.',
    keyHelp:
      'Key: the name the sender puts in channel_key, for example temperature. Letters, digits, _ . and - only.',
    nameColumnHelp: 'Name: what the app shows, for example Living room temperature. Optional.',
    unitHelp:
      'Unit: the unit every reading must state, for example degC, percent or hPa. It cannot change later.',
    typeHelp: 'Type: number for measurements, integer for counts, boolean for on/off, text for states.',
    presets: 'Start from:',
    preset: {
      weather: 'Weather station',
      power: 'Power meter',
      single: 'One value',
    },
    presetNames: {
      temperature: 'Temperature',
      humidity: 'Humidity',
      pressure: 'Pressure',
      power: 'Power',
      energy: 'Energy',
    },
    channelKey: 'Key',
    channelName: 'Name',
    channelNamePlaceholder: 'Optional',
    channelUnit: 'Unit',
    channelType: 'Type',
    channelTypeNumber: 'Number',
    channelTypeInteger: 'Integer',
    channelTypeBoolean: 'On/off',
    channelTypeString: 'Text',
    addChannel: 'Add channel',
    remove: 'Remove',
    removeChannel: 'Remove channel {index}',
    undeclaredHint:
      'A reading for a key you did not declare creates that channel with the unit and type it arrives with.',
    channelKeyRequired: 'Every channel needs a key.',
    channelKeyFormat: 'Channel keys may use letters, digits, _ . and - only, up to 64 characters.',
    channelUnitRequired: 'Every channel needs a unit.',
    channelKeyUnique: 'Channel keys must be unique.',
    nameRequired: 'Enter a feed name.',
    atLeastOneChannel: 'Declare at least one channel.',
    create: 'Create feed',
    creating: 'Creating…',
    createError: 'Could not create the feed.',
    createdTitle: 'Feed created',
    createdDescription: 'Copy the token, then point your sender at the address below.',
    token: 'Token',
    tokenOnce:
      'This token is shown once and cannot be retrieved again. Store it now; if you lose it, issue a new one from the feed’s page.',
    tokenKeepPrivate:
      'Anyone with the token can send data as this feed. Keep it out of links, logs and screenshots.',
    copyToken: 'Copy token',
    goToSource: 'Open the feed',
    backToSources: 'Back to sources',
  },
  intake: {
    title: 'Sending data',
    connector: 'Sends by',
    lastSample: 'Last reading received',
    noSampleYet: 'Nothing yet',
    tokenUsed: 'Token last used',
    tokenNeverUsed: 'Not yet',
    noToken: 'No active token',
    newToken: 'New token',
    newTokenTitle: 'Issue a new token?',
    newTokenBodyHttp:
      'The current token stops working at once. Update your sender with the new one, which is shown only once.',
    newTokenBodyMqtt:
      'The current password stops working for new connections; a client that is connected now stays connected until it reconnects. The new token is shown only once.',
    newTokenConfirm: 'Replace token',
    newTokenShown: 'Your new token. It is shown only now; the commands below include it.',
    rejectionsTitle: 'Recently refused readings',
    rejectionsHint:
      'These readings arrived but were not stored. Fix the sender and send them again with the same measurement ID.',
    rejectedAt: 'When',
    reason: 'Why',
    measurement: 'Measurement ID',
    reasons: {
      malformed: 'Not a valid reading: a field is missing, misspelt or of the wrong kind.',
      unsupported_version: 'protocol_version must be 0.',
      invalid_time:
        'The time does not fit time_quality: give observed_at with a time zone, or null with unknown.',
      invalid_value: 'The value does not fit the channel’s type, or cannot be stored exactly.',
      oversized: 'The message is too large.',
      channel_limit: 'This feed has reached its channel limit, so a new key was refused.',
      conflicting_content:
        'This measurement ID was already used for a different reading. Use a new ID for each reading.',
      channel_mismatch:
        'The unit or type differs from the channel’s. Send the unit the channel was created with.',
      source_unowned: 'The feed was removed.',
    },
    guideTitle: 'How to send data',
    whereTitle: 'Where to send it',
    httpWhere: 'Send one HTTPS request per reading:',
    mqttWhere:
      'Connect over TLS. The broker uses a normal public certificate, so no certificate of your own is needed.',
    method: 'Method',
    url: 'URL',
    header: 'Header',
    headerValue: 'Header value',
    theToken: 'Your token',
    contentType: 'Content-Type',
    host: 'Host',
    port: 'Port',
    portTls: '{port} (TLS)',
    username: 'Username',
    password: 'Password',
    clientId: 'Client ID',
    clientIdHint: 'Anything unique, for example the username',
    topic: 'Topic',
    qos: 'QoS',
    qosHint: '1, not retained',
    whatTitle: 'What to send',
    oneSampleHttp: 'The body is one reading as JSON. For several values, send one request per channel.',
    oneSampleMqtt:
      'Each message is one reading as JSON. For several values, publish one message per channel.',
    field: 'Field',
    meaning: 'Meaning',
    fieldProtocol: 'Always 0.',
    fieldMeasurement:
      'Your ID for this reading, unique within the feed. Send the same ID again when retrying, so a reading is never stored twice; the time plus the channel key works well.',
    fieldChannel: 'The channel’s key.',
    fieldValue: 'The reading: a number, an integer, true or false, or text, matching the channel’s type.',
    fieldUnit: 'Exactly the channel’s unit.',
    fieldObserved:
      'When it was measured, as ISO 8601 with a time zone, for example 2026-10-19T12:00:00Z. Use null if unknown.',
    fieldQuality:
      'synchronized if the sender’s clock is set by NTP, estimated if it may be off, unknown when observed_at is null.',
    tryTitle: 'Try it',
    exampleChannel: 'Example for channel',
    payload: 'Reading',
    curl: 'With curl',
    mosquitto: 'With mosquitto_pub',
    copy: 'Copy',
    placeholderHint: 'Replace {placeholder} with your token.',
    httpResponses:
      'Answers: 200 stored (or duplicate, already stored); 400 not a valid reading; 401 wrong token; 422 refused, see the reason; 429 too fast, retry later; 503 try again.',
    mqttResponses:
      'The broker accepts every message it is allowed to; a reading that is refused afterwards is listed on this feed’s page with the reason.',
  },
  admin: {
    title: 'Administration',
    description: 'Manage accounts and devices, act as another account, and review the audit log.',
    tabs: {
      users: 'Accounts',
      modules: 'Modules',
      devices: 'Devices',
      audit: 'Audit log',
      firmware: 'Firmware',
    },
    firmware: {
      title: 'Firmware releases',
      intro:
        'Build and sign a release on your machine with scripts/device.sh release in the firmware repository, then upload the image and its .release.json record. Owners of devices running an older version are offered it on the device page; nothing installs until they start it.',
      image: 'Image (.bin)',
      record: 'Release record (.release.json)',
      publish: 'Publish',
      publishing: 'Publishing…',
      recordInvalid: 'That file is not a release record from device.sh release.',
      published: 'Release {version} published.',
      empty: 'No firmware releases yet',
      columnVersion: 'Version',
      columnInstallsOn: 'Installs on',
      columnImage: 'Image',
      columnPublished: 'Published',
      columnStatus: 'Status',
      columnActions: 'Actions',
      sequence: 'Offer {sequence}',
      layout: 'layout {layout}',
      size: '{size} KiB',
      windowAny: 'Any older version',
      windowFrom: '{min} and later',
      windowUpTo: 'Up to {max}',
      windowBetween: '{min} to {max}',
      offered: 'Offered',
      withdrawn: 'Withdrawn',
      withdraw: 'Withdraw',
      restore: 'Restore',
      withdrawHint: 'Withdrawing stops new offers; updates already requested continue.',
    },
    devices: {
      title: 'Enrolled devices',
      intro:
        'A device can pair once its factory certificate is enrolled here. In the firmware repository, scripts/device.sh manufacture <id> makes the certificate, writes the factory partition and names the file to choose.',
      enroll: 'Enroll a device',
      publicId: 'Public id',
      publicIdHint: 'As written into the factory partition, for example sen-0001.',
      certificate: 'Factory certificate (.pem)',
      certificateHint: 'devices/<id>.pem in the device PKI directory.',
      enrolled: '{id} is enrolled.',
      certificateAdded: 'A further certificate was added to {id}.',
      alreadyEnrolled: '{id} already has this certificate.',
      empty: 'No devices are enrolled yet.',
      columnDevice: 'Device',
      columnOwner: 'Owner',
      columnSeen: 'Last contact',
      columnFirmware: 'Firmware',
      columnCertificates: 'Certificates',
      columnActions: 'Actions',
      unowned: 'Unowned',
      neverSeen: 'Never',
      certificateActive: 'Active',
      certificateRevoked: 'Revoked: {reason}',
      reset: 'Reset',
      resetTitle: 'Reset {id}?',
      resetBody:
        'The next time it connects, the device erases its Wi-Fi settings, modules and stored readings and opens setup. It keeps its owner: pairing it again from that account restores its configuration.',
      resetQueued: '{id} resets the next time it connects.',
      revoke: 'Revoke',
      revokeTitle: 'Revoke this certificate?',
      revokeBody:
        'The device can no longer pair or connect with it. This cannot be undone; a new certificate needs a new factory partition.',
      reason: 'Reason',
      reasonLost: 'Lost',
      reasonCompromised: 'Compromised',
      reasonRetired: 'Retired',
    },
    modules: {
      title: 'Module catalog',
      intro:
        'Publish modules built and signed with smf (`smf catalog` builds them all). Choose a .smpk package and its .artifact.json record; the server checks the signature against the trusted catalog keys and stores the package.',
      package: 'Package (.smpk)',
      record: 'Signature record (.artifact.json)',
      publish: 'Publish',
      publishing: 'Publishing…',
      published: 'Published {module} {version}',
      alreadyPublished: '{module} {version} was already published with these bytes.',
      recordInvalid: 'That record is not a .artifact.json file from smf.',
      empty: 'No modules are published yet.',
      columnModule: 'Module',
      columnVersion: 'Version',
      columnProvider: 'Source',
      columnPublished: 'Published',
      columnStatus: 'Status',
      columnActions: 'Actions',
      selectable: 'Selectable',
      deprecated: 'Deprecated',
      builtIn: 'Built in',
      signed: 'Signed package',
      deprecate: 'Deprecate',
      restore: 'Restore',
      deprecateHint:
        'A deprecated version stays on devices that already use it but cannot be newly selected.',
    },
    usersTitle: 'Accounts',
    usersEmpty: 'No accounts are visible.',
    usersEmptyHint: 'Accounts appear here once users sign in.',
    columnId: 'Account',
    columnRole: 'Role',
    columnStatus: 'Status',
    columnCreatedAt: 'Created',
    columnActions: 'Actions',
    statusActive: 'Active',
    statusDisabled: 'Disabled',
    statusDeleted: 'Deleted',
    columnAccess: 'Access',
    approved: 'Approved',
    awaitingApproval: 'Waiting for approval',
    approve: 'Approve',
    revokeApproval: 'Withdraw approval',
    deleteUser: 'Delete',
    deleteUserTitle: 'Delete this account?',
    deleteUserBody:
      'Delete {target} and everything it owns, at once and for good? Its devices are released and reset.',
    requireApproval: 'Closed beta: new accounts need approval',
    requireApprovalHint:
      'Anyone can sign in with Google or Apple, which creates the account. Until you approve it, it only sees that it is waiting. Administrators are never held back.',
    changeRole: 'Change role',
    roleDialogTitle: 'Change role',
    roleDialogBody: 'Set {target} to {role}?',
    grantAdminTitle: 'Grant administrator access',
    grantAdminBody: 'Make {target} an administrator? This is not limited to their own account.',
    grantAdminList:
      'An administrator can list every account on this deployment, approve or withdraw approval, change anyone’s role, act as any account, enroll and revoke devices, publish the module catalog and firmware, and delete accounts.',
    grantAdminSelf: 'This is your own account, so the change takes effect on your next request.',
    makeAdmin: 'Make administrator',
    makeOwner: 'Make owner',
    roleSet: 'Role updated',
    roleError: 'Could not change the role.',
    roleNoop: 'This account already has that role.',
    impersonate: 'Act as this account',
    impersonateConfirmTitle: 'Act as another account?',
    impersonateConfirmBody:
      'You will act as {target}. Every change you make is recorded in the audit log. Your admin operations are hidden until you stop.',
    selfDemotionWarning: 'Changing your own role removes your admin access immediately.',
    auditTitle: 'Audit log',
    auditEmpty: 'No audit entries yet.',
    auditEmptyHint: 'Role changes, impersonation, device actions and impersonated changes appear here.',
    auditColumnActor: 'Actor',
    auditColumnEffective: 'Effective account',
    auditColumnTarget: 'Target',
    auditActionRoleSet: 'Role changed',
    auditActionImpersonateStart: 'Impersonation started',
    auditActionImpersonateStop: 'Impersonation stopped',
    auditActionImpersonateMutation: 'Change while impersonating',
    auditActionDeviceEnroll: 'Device enrolled',
    auditActionDeviceRevoke: 'Certificate revoked',
    auditActionDeviceReset: 'Device reset',
    auditActionApprovalSet: 'Approval changed',
    auditActionAccessSet: 'Closed beta switched',
    auditActionAccountDelete: 'Account deleted',
    auditActionModulePublish: 'Module published',
    auditActionFirmwarePublish: 'Firmware release published',
    auditActionUnknown: '{action}',
    auditFactRole: 'Role',
    auditFactApproved: 'Approved',
    auditFactRequireApproval: 'Approval required',
    auditFactFingerprint: 'Fingerprint',
    auditFactCertificateStatus: 'Certificate',
    auditFactReason: 'Reason',
    auditFactDevice: 'Device',
    auditFactRevision: 'Revision',
    auditFactDigest: 'Digest',
    auditFactSize: 'Size',
    auditFactEpoch: 'Security epoch',
    auditFactSigningKey: 'Signing key',
    auditFactSequence: 'Offer sequence',
    auditFactBoard: 'Board',
    auditStatus: 'HTTP {status}',
    auditNoFurtherDetails: 'No further details were recorded.',
    auditShowRaw: 'Show raw record',
    auditHideRaw: 'Hide raw record',
    accessTitle: 'Sign-up policy',
    accountActions: 'Account',
    dangerZone: 'Danger zone',
    moreActions: 'More actions',
    blockedWhileImpersonating: 'Administrative actions are unavailable while impersonating.',
  },
  impersonation: {
    banner: 'Acting as {effective} (administrator {actor})',
    stop: 'Stop impersonating',
    stopping: 'Stopping…',
    started: 'Now acting as {target}',
    stopped: 'Impersonation stopped',
    error: 'Could not start impersonation.',
    stopError: 'Could not stop impersonation.',
    resumed: 'This session was already acting as another account when the app opened.',
  },
  install: {
    title: 'Install Sensing to continue',
    required: 'On your phone, Sensing runs as an app.',
    iosShare: 'Tap Share',
    iosShareHint: 'Not in the toolbar? Open',
    iosAdd: 'Tap Add to Home Screen',
    androidMenu: 'Tap the browser menu',
    androidAdd: 'Tap Install app or Add to Home screen',
    open: 'Open Sensing from your home screen',
    menuHere: 'Menu',
    toolbarBelow: "Share is in Safari's toolbar",
    featureReadings: 'Live readings from every sensor',
    featureDevices: 'Add and set up devices in a few taps',
    featureRemote: 'Change modules, Wi-Fi and timing from anywhere',
  },
  offline: {
    title: 'You are offline',
    body: 'The app shell is available, but source data cannot be loaded right now.',
    unavailable: 'Data is unavailable while offline.',
    retry: 'Retry',
    sessionUnconfirmed:
      'Signed out locally. The server could not be reached, so the session may still be active.',
  },
  removal: {
    action: 'Remove',
    archiveAction: 'Delete',
    deviceTitle: 'Remove this device?',
    externalTitle: 'Remove this feed?',
    archiveTitle: 'Delete this history?',
    deviceBody:
      'The device leaves your account and stops running its modules. Anyone can then pair it with a code.',
    externalBody: 'The feed leaves your account and its token stops working.',
    archiveBody: 'Every stream and reading in it is deleted.',
    dataLabel: 'Its readings',
    keep: 'Keep them',
    keepHint: 'They move to “Removed”, where you can still read or delete them.',
    delete: 'Delete them',
    deleteHint: 'Every stream and reading of this source is deleted.',
    releaseAction: 'Release',
    releaseTitle: 'Release this device?',
    releaseBody:
      'The device leaves your account. The next time it connects, it erases its Wi-Fi settings, modules and stored readings and opens setup, so anyone can pair it with a code.',
    releaseOffline: 'If it is switched off now, it resets the next time it connects.',
    confirmRelease: 'Release',
    confirmReleaseDelete: 'Release and delete',
    irreversible: 'This cannot be undone.',
    confirmKeep: 'Remove',
    confirmDelete: 'Remove and delete',
    archiveConfirm: 'Delete',
  },
  deletion: {
    action: 'Delete…',
    title: 'Delete data from {name}',
    what: 'What to delete',
    before: 'Readings before a date',
    date: 'Before',
    dateHint: 'Readings from this day on stay.',
    all: 'All readings',
    allHint: 'The stream stays and new readings keep arriving.',
    stream: 'The whole stream',
    streamHint: 'The stream and all its readings.',
    streamActiveHint:
      'The stream and all its readings. The source still sends it, so it starts again, empty.',
    confirm: 'Delete',
    done: { one: '{count} reading deleted.', other: '{count} readings deleted.' },
  },
  controls: {
    title: 'Controls',
    applied: 'The device runs these settings.',
    pending: 'Waiting for the device to apply the latest change…',
    auto: 'Automatic',
    on: 'On',
    off: 'Off',
    saving: 'Saving',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    invalid_request: 'The request was not valid.',
    unauthenticated: 'Your session is no longer valid. Please sign in again.',
    forbidden: 'You are not allowed to do that.',
    not_found: 'We could not find what you asked for.',
    conflict: 'This changed since you loaded it. The latest version is shown now; try again.',
    rate_limited: 'Too many requests. Wait a moment and try again.',
    claim_rejected: 'The pairing code was rejected, expired, or already used.',
    device_owned_elsewhere: 'This device belongs to another account. Its owner must remove it first.',
    payload_too_large: 'The request was too large.',
    unprocessable: 'The request could not be processed as sent.',
    internal_error: 'The service had a problem. Please try again.',
    unavailable: 'The service is temporarily unavailable. Please try again.',
  },
  a11y: {
    skipToContent: 'Skip to main content',
    currentPage: 'Current page',
    loadingData: 'Loading data',
    impersonationBanner: 'Impersonation notice',
    chartImage: 'History chart',
  },
} as const;

export type CatalogShape = typeof en;
